import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Lock, Plus, Trash2, Pencil, BookMarked, FileDown } from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };

export default function Diary() {
  const [entries, setEntries] = useState(null);
  const [reflections, setReflections] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', body: '' });
  const { t, dateLocale } = useI18n();

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(dateLocale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return ''; }
  };

  const load = () => {
    api.get('/diary').then((r) => setEntries(r.data.entries)).catch(() => {});
    api.get('/diary/reflections').then((r) => setReflections(r.data.reflections)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing('new'); setForm({ title: '', body: '' }); };
  const startEdit = (e) => { setEditing(e.entry_id); setForm({ title: e.title, body: e.body }); };

  const save = async () => {
    if (!form.title.trim() && !form.body.trim()) return;
    try {
      if (editing === 'new') await api.post('/diary', form);
      else await api.put(`/diary/${editing}`, form);
      toast.success(t('diary.saved'));
      setEditing(null);
      load();
    } catch { toast.error(t('common.error')); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/diary/${id}`);
      toast.success(t('diary.deleted'));
      load();
    } catch { toast.error(t('common.error')); }
  };

  const exportPdf = async () => {
    try {
      const r = await api.get('/diary/export.pdf', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mon-carnet-heritage.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('diary.exported'));
    } catch { toast.error(t('account.exportError')); }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="diary-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme">{t('diary.title')}</h1>
            <p className="text-base text-brume mt-3 flex items-center gap-2">
              <Lock className="w-4 h-4 text-or-400" strokeWidth={2.5} />
              {t('diary.subtitle')}
            </p>
          </div>
          <div className="flex gap-2">
            {!editing && (
              <Button onClick={exportPdf} data-testid="diary-export-button" variant="outline"
                className="rounded-full border-2 border-or-400/60 text-or-300 font-bold gap-2 bg-transparent hover:bg-or-400/10">
                <FileDown className="w-4 h-4" strokeWidth={2.5} /> {t('diary.export')}
              </Button>
            )}
            {!editing && (
              <Button onClick={startNew} data-testid="diary-new-button"
                className="rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold gap-2 shadow-md hover:-translate-y-0.5 transition-transform">
                <Plus className="w-5 h-5" strokeWidth={2.5} /> {t('diary.new')}
              </Button>
            )}
          </div>
        </div>
      </motion.div>

      {editing && (
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={spring}
          className="diary-lines rounded-3xl p-8 mt-8 shadow-[0_8px_30px_rgb(0,0,0,0.3)]"
          data-testid="diary-editor">
          <Input data-testid="diary-title-input" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={t('diary.titlePlaceholder')}
            className="bg-transparent border-none font-caveat text-3xl text-slate-800 placeholder:text-slate-400 focus-visible:ring-0 px-0 h-auto mb-2" />
          <textarea data-testid="diary-body-input" value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder={t('diary.bodyPlaceholder')} rows={8}
            className="diary-textarea w-full font-caveat text-2xl text-slate-800 placeholder:text-slate-400" />
          <div className="flex gap-3 mt-4">
            <Button onClick={save} data-testid="diary-save-button"
              className="rounded-full bg-nuit-800 hover:bg-nuit-700 text-creme font-bold px-8">{t('diary.save')}</Button>
            <Button onClick={() => setEditing(null)} variant="ghost" data-testid="diary-cancel-button"
              className="rounded-full text-slate-500 font-bold hover:bg-transparent">{t('common.cancel')}</Button>
          </div>
        </motion.div>
      )}

      {reflections.length > 0 && (
        <div className="mt-10">
          <h2 className="text-xl font-semibold text-creme mb-4 flex items-center gap-2" data-testid="reflections-title">
            <BookMarked className="w-5 h-5 text-or-400" strokeWidth={2.5} /> {t('diary.reflections')}
          </h2>
          <div className="space-y-4">
            {reflections.map((r, i) => (
              <div key={i} className="bg-nuit-800 border border-nuit-700 rounded-2xl p-5" data-testid={`reflection-${r.chapter}-${r.key}`}>
                <p className="text-xs font-bold uppercase tracking-widest text-or-400">
                  {t('common.chapterN', { n: r.chapter })} — {r.key === 'carnet' ? t('diary.myNotebook') : t('diary.beforeTurning')}
                </p>
                <p className="text-base text-creme mt-2 leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-8 mt-10">
        {(entries || []).map((e, i) => (
          <motion.article key={e.entry_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: i * 0.06 }}
            className="diary-lines rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.25)]"
            data-testid={`diary-entry-${e.entry_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-amber-700">{formatDate(e.created_at)}</p>
                <h2 className="font-caveat text-3xl font-semibold text-slate-800 mt-1">{e.title || t('diary.untitled')}</h2>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => startEdit(e)} data-testid={`diary-edit-${e.entry_id}`}
                  className="w-9 h-9 rounded-full hover:bg-amber-100 flex items-center justify-center text-slate-500">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(e.entry_id)} data-testid={`diary-delete-${e.entry_id}`}
                  className="w-9 h-9 rounded-full hover:bg-rose-100 flex items-center justify-center text-slate-500 hover:text-rose-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p className="font-caveat text-2xl text-slate-700 whitespace-pre-wrap mt-3" style={{ lineHeight: '36px' }}>{e.body}</p>
          </motion.article>
        ))}
        {entries && entries.length === 0 && !editing && (
          <div className="text-center py-16 text-brume" data-testid="diary-empty">
            <p className="font-caveat text-3xl">{t('diary.empty')}</p>
          </div>
        )}
        {!entries && <p className="text-brume" data-testid="diary-loading">{t('common.loading')}</p>}
      </div>
    </div>
  );
}
